import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import abilities from "@/abilities";
import Error403 from "@/domains/error/Error403.vue";
import Error404 from "@/domains/error/Error404.vue";
import Home from "@/domains/home/Home.vue";
import Auth from "@/domains/auth/Auth.vue";
import PasswordReset from "@/domains/auth/PasswordReset/PasswordReset.vue";
import Login from "@/domains/auth/Login/Login.vue";
import Accounts from "@/domains/home/services/accounts/Accounts.vue";
import URSBRegistry from "@/domains/home/services/registry/URSBRegistry.vue";
import DocumentRegistry from "@/domains/home/services/registry/legal-documents/DocumentRegistry.vue";
import PractitionerRegistry from "@/domains/home/services/registry/practitioners/PractitionerRegistry.vue";
import EntityFileWrapper from "@/domains/home/services/registry/business/files/EntityFileWrapper.vue";
import LegalDocumentFileWrapper from "@/domains/home/services/registry/legal-documents/LegalDocumentFileWrapper.vue";
import PractitionerProfileWrapper from "@/domains/home/services/registry/practitioners/PractitionerProfileWrapper.vue";
import AccountClassifications from "@/domains/home/services/accounts/AccountClassifications.vue";
import PermissionsManagement from "@/domains/home/services/accounts/PermissionsManagement.vue";
import ServiceCost from "@/domains/home/services/billing/views/ServiceCost.vue";
import URSBTaxHeads from "@/domains/home/services/billing/views/URSBTaxHeads.vue";
import BillingAndRevenue from "@/domains/home/services/billing/BillingAndRevenue.vue";
import Configurations from "@/domains/home/services/configurations/Configurations.vue";
import InternalProcesses from "@/domains/home/services/internal_processing/InternalProcesses.vue";
import PRNManager from "@/domains/home/services/billing/views/PRNManager.vue";
import DocumentCost from "@/domains/home/services/billing/views/DocumentCost.vue";
import Transactions from "@/domains/home/services/billing/views/Transactions.vue";
import HomeDashboard from "@/domains/home/services/HomeDashboard.vue";
import MDARegistry from "@/domains/home/services/registry/mdas/MDARegistry.vue";
import AccountSummary from "@/domains/home/services/accounts/views/AccountSummary.vue";
import NonIndividualOperations from "@/domains/home/services/nir/NonIndividualOperations.vue";
import NIRAssociateRegisters from "@/domains/home/services/nir/associate-registers/NIRAssociateRegisters.vue";
import NIRForms from "@/domains/home/services/nir/forms/NIRForms.vue";
import MasterData from "@/domains/home/services/nir/configurations/MasterData.vue";
import NewRegistration from "@/domains/home/services/nir/registration/NewRegistration.vue";
import NIRRegistry from "@/domains/home/services/registry/non-individuals/NIRRegistry.vue";
import DataFields from "@/domains/home/services/nir/configurations/views/DataFields.vue";
import EntityTypes from "@/domains/home/services/nir/configurations/views/EntityTypes.vue";
import FieldLookups from "@/domains/home/services/nir/configurations/views/FieldLookups.vue";
import FormComposer from "@/domains/home/services/nir/forms/views/FormComposer.vue";
import FormList from "@/domains/home/services/nir/forms/views/FormList.vue";
import FormDetails from "@/domains/home/services/nir/forms/views/FormDetails.vue";
import Services from "@/domains/home/services/nir/configurations/views/Services.vue";
import Registrations from "@/domains/home/services/nir/registration/Registrations.vue";
import RegistrationApplications from "@/domains/home/services/nir/registration/RegistrationApplications.vue";
import NIRTaskQueue from "@/domains/home/services/nir/registration/NIRTaskQueue.vue";
import AssociateRegister from "@/domains/home/services/nir/associate-registers/AssociateRegister.vue";
import BulkDataApplications from "@/domains/home/services/nir/registration/BulkDataApplications.vue";
import BulkDataDetails from "@/domains/home/services/nir/registration/views/BulkDataDetails.vue";
import NIREntityFile from "@/domains/home/services/registry/non-individuals/NIREntityFile.vue";
import BusinessNameRegistry from "@/domains/home/services/registry/business/BusinessNameRegistry.vue";
import CompanyRegistry from "@/domains/home/services/registry/business/CompanyRegistry.vue";
import BuildingSocietyRegistry from "@/domains/home/services/registry/business/BuildingSocietyRegistry.vue";
import BlackListRegistry from "@/domains/home/services/registry/business/BlackListRegistry.vue";
import DeregistrationRegistry from "@/domains/home/services/registry/business/DeregistrationRegistry.vue";
import ContactConfigs from "@/domains/home/services/nir/configurations/views/ContactConfigs.vue";
import RegulationForms from "@/domains/home/services/nir/configurations/views/RegulationForms.vue";
import ForcedRegistrationApplications
  from "@/domains/home/services/nir/registration/ForcedRegistrationApplications.vue";
import DataUpdateApplications from "@/domains/home/services/nir/registration/DataUpdateApplications.vue";
import RegistrationStatusConfig from "@/domains/home/services/nir/configurations/views/RegistrationStatusConfig.vue";
import ObrsFormUpdate from "@/domains/home/services/nir/configurations/views/ObrsFormUpdate.vue";
import AmendmentApplications from "@/domains/home/services/nir/registration/AmendmentApplications.vue";
import Notifications from "@/domains/home/services/notifications/Notifications.vue";
import DefaultRegistrar from "@/domains/home/services/default-registrar/DefaultRegistrar.vue";
import ActivityLogs from "@/domains/home/services/activity-logs/ActivityLogs.vue";
import Monitoring from "@/domains/home/services/monitoring/Monitoring.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: Auth,
    redirect: '/login',
    name: "login",
    meta: {
      title: "Login"
    },
    children: [
      {
        path: '/login',
        component: Login,
        name: "login-page"
      },
      {
        path: '/reset-password',
        component: PasswordReset,
        name: "reset-password"
      },
    ]
  },
  {
    path: "/forbidden",
    name: "forbidden",
    component: Error403,
    meta: {
      title: "Unauthorized",
      requiresAuth: true,
      resource: "forbidden",
      action: "read"
    },
  },

  {
    path: "/home",
    component: Home,
    redirect: "/home/dashboard",
    name: "Home",
    meta: {
      requiresAuth: true,
      resource: "general",
      action: "can_access_dashboard",
    },
    children: [
      {
        name: "user_profile",
        path: "/home/dashboard",
        component: HomeDashboard,
        meta: { requiresAuth: true, title: "", resource: "general", action: "can_access_dashboard" }
      },
      {
        path: "/home/accounts",
        name: "accounts",
        component: Accounts,
        redirect: "/home/accounts/views",
        meta: { requiresAuth: true, resource: "general", action: "can_access_system_accounts", },
        children: [
          {
            path: "/home/accounts/views",
            component: AccountClassifications,
            name: "accounts-views",
            meta: { action: "can_access_system_accounts", resource: "general", },
          },
          {
            path: "/home/accounts/summary",
            component: AccountSummary,
            name: "account-summary",
            meta: { action: "can_view_accounts_breakdown", resource: "general", },
          },
          {
            path: "/home/accounts/permissions",
            component: PermissionsManagement,
            name: "permission-views",
            meta: { action: "can_view_permission_management", resource: "general", },
          },
        ]
      },
      {
        path: "/home/registry",
        name: "registry",
        component: URSBRegistry,
        meta: {
          requiresAuth: true, action: "can_view_registry",
          resource: "general",
        },
        children: [
          {
            path: "/home/registry/business-names",
            component: BusinessNameRegistry,
            name: "business-name-registry",
            meta: {
              title: "Business Name Registry", action: "can_view_mda_registry",
              resource: "general"
            }
          },
          {
            path: "/home/registry/companies",
            component: CompanyRegistry,
            name: "company-registry",
            meta: {
              title: "Company Registry", action: "can_view_mda_registry",
              resource: "general"
            }
          },
          {
            path: "/home/registry/black-list",
            component: BlackListRegistry,
            name: "blacklist-registry",
            meta: {
              title: "Blacklist Registry", action: "can_view_mda_registry",
              resource: "general"
            }
          },
          {
            path: "/home/registry/deregistered",
            component: DeregistrationRegistry,
            name: "de-registered-registry",
            meta: {
              title: "DeRegistered Registry", action: "can_view_mda_registry",
              resource: "general"
            }
          },
          {
            path: "/home/registry/building-societies",
            component: BuildingSocietyRegistry,
            name: "building-society-registry",
            meta: {
              title: "Building Society Registry", action: "can_view_mda_registry",
              resource: "general"
            }
          },
          {
            path: "/home/registry/mda",
            component: MDARegistry,
            name: "mda-registry",
            meta: {
              title: "MDA Registry", action: "can_view_mda_registry",
              resource: "general",
            },
          },

          {
            path: "/home/registry/practitioners",
            component: PractitionerRegistry,
            name: "practitioners-registry",
            meta: {
              title: "Practitioner Registry", action: "can_view_practitioner_registry",
              resource: "general",
            }
          },
          {
            path: "/home/registry/file-viewer/:id",
            component: EntityFileWrapper,
            name: "file-viewer",
            meta: {
              title: "File - URSB Registry", action: "can_view_reservation_settings",
              resource: "general",
            }
          },
          {
            path: "/home/registry/document-viewer/:id",
            component: LegalDocumentFileWrapper,
            name: "document-viewer",
            meta: {
              title: "Document - URSB Registry", action: "can_view_reservation_settings",
              resource: "general",
            }
          },
          {
            path: "/home/registry/practitioner-viewer/:id",
            component: PractitionerProfileWrapper,
            name: "practitioner-viewer",
            meta: {
              title: "Practitioner - URSB Registry", action: "can_view_reservation_settings",
              resource: "general",
            }
          },
          {
            path: "/home/registry/documents",
            component: DocumentRegistry,
            name: "documents-registry",
            meta: {
              title: "Document Registry", action: "can_view_doc_registry",
              resource: "general",
            }
          },
          {
            path: "/home/registry/nonindividuals",
            component: NIRRegistry,
            name: "nonindividual-registry",
            meta: {
              title: "Non Individual - URSB Registry", action: "can_view_reservation_settings",
              resource: "general",
            }
          },

        ]
      },
      {
        path: "/home/nonindividual/:id",
        component: NIREntityFile,
        name: "nonindividual-viewer",
      },
      {
        path: "/home/billing",
        name: "billing",
        component: BillingAndRevenue,
        meta: { requiresAuth: true, resource: "general", action: "can_view_billing_settings" },
        children: [
          {
            path: "/home/billing/service-costs",
            component: ServiceCost,
            name: "service-costs",
            meta: { action: "can_view_billing_service_cost_settings", resource: "general" }
          },
          {
            path: "/home/billing/document-costs",
            component: DocumentCost,
            name: "document-costs",
            meta: { action: "can_view_billing_document_cost_settings", resource: "general" }
          },
          {
            path: "/home/billing/tax-heads",
            component: URSBTaxHeads,
            name: "tax-heads",
            meta: { action: "can_view_billing_tax_heads_settings", resource: "general" }
          },
          {
            path: "/home/billing/transactions",
            component: Transactions,
            name: "transactions",
            meta: { action: "can_view_billing_transactions_settings", resource: "general" }
          },
          {
            path: "/home/billing/prn-manager",
            component: PRNManager,
            name: "prn-manager",
            meta: { action: "can_view_billing_prn_settings", resource: "general" }
          },
        ]
      },

      {
        path: "/home/nir",
        name: "nir",
        component: NonIndividualOperations,
        meta: { requiresAuth: true, resource: "general", action: "can_access_nir" },
        children: [
          {
            path: "/home/nir/registration",
            name: "nir-registration",
            component: Registrations,
            redirect:{name:'nir-registration-applications'},
            meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
            children:[
              {
                path: "/home/nir/registration/applications",
                name: "nir-registration-applications",
                component: RegistrationApplications,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/forced-applications",
                name: "nir-registration-forced-applications",
                component: ForcedRegistrationApplications,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/data-update-applications",
                name: "nir-registration-data-update-applications",
                component: DataUpdateApplications,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/amendment-applications",
                name: "nir-registration-amendment-applications",
                component: AmendmentApplications,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/bulk-applications",
                name: "nir-bulk-applications",
                component: BulkDataApplications,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/bulk-applications/:id",
                name: "nir-bulk-application-details",
                component: BulkDataDetails,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/queue",
                name: "nir-registration-task-queue",
                component: NIRTaskQueue,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              },
              {
                path: "/home/nir/registration/new",
                name: "nir-registration-new",
                component: NewRegistration,
                meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
              }
            ]
          },
          {
            path: "/home/nir/forms",
            name: "nir-forms",
            component: NIRForms,
            redirect:{name:'nir-forms-list'},
            meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
            children:[
              {
                path: "/home/nir/form/list",
                name: "nir-forms-list",
                component: FormList,
              },
              {
                path: "/home/nir/form/client/:id",
                name: "nir-forms-client",
                component: FormDetails,
              },
              {
                path: "/home/nir/form/data/:id",
                name: "nir-forms-data",
                component: FormComposer,
              }
            ]
          },
          {
            path: "/home/nir/master-data",
            name: "nir-master-data",
            component: MasterData,
            redirect:{name:'nir-master-data-fields'},
            meta: { requiresAuth: true, resource: "general", action: "can_access_nir_forms" },
            children:[
              {
                path: "/home/nir/master-data/fields",
                name: "nir-master-data-fields",
                component: DataFields,
              },
              {
                path: "/home/nir/master-data/lookups",
                name: "nir-master-data-lookups",
                component: FieldLookups,
              },
              {
                path: "/home/nir/master-data/types",
                name: "nir-master-data-types",
                component: EntityTypes,
              },
              {
                path: "/home/nir/master-data/services",
                name: "nir-master-data-services",
                component: Services,
              },
              {
                path: "/home/nir/master-data/contacts",
                name: "nir-master-data-contacts",
                component: ContactConfigs,
              },
              {
                path: "/home/nir/master-data/regulation-forms",
                name: "nir-master-data-regulation-forms",
                component: RegulationForms,
              },
              {
                path: "/home/nir/master-data/registration-statuses",
                name: "nir-master-data-registration-statuses",
                component: RegistrationStatusConfig,
              },
              {
                path: "/home/nir/master-data/obrs-form-update",
                name: "nir-master-data-obrs-form-update",
                component: ObrsFormUpdate,
              }
            ]
          },
          {
            path: "/home/nir/associate-registers",
            name: "nir-associate-registers",
            component: NIRAssociateRegisters,
            meta: { requiresAuth: true, resource: "general", action: "can_access_nir_institutions" }
          },
          {
            path: "/home/nir/associate-register/:id",
            name: "nir-associate-register",
            component: AssociateRegister,
            meta: { requiresAuth: true, resource: "general", action: "can_access_nir_institutions" }
          },
        ]
      },
      {
        path: "/home/settings",
        name: "configurations",
        component: Configurations,
        meta: { requiresAuth: true, resource: "general", action: "can_view_reservation_settings" }
      },

      {
        path: "/home/internal-processes",
        name: "internal-processes",
        component: InternalProcesses,
        meta: { requiresAuth: true, resource: "general", action: "can_access_internal_processing" }
      },
      {
        path: "/home/notifications",
        name: "notifications",
        component: Notifications,
        meta: { requiresAuth: true, resource: "general", action: "can_view_general_settings" }
      },
      {
        path: "/home/default-registrar",
        name: "default-registrar",
        component: DefaultRegistrar,
        meta: { requiresAuth: true, resource: "general", action: "can_view_general_settings" }
      },
      {
        path: "/home/activity-logs",
        name: "activity-logs",
        component: ActivityLogs,
        meta: { requiresAuth: true, resource: "general", action: "can_view_api_activity_logs" }
      },
      {
        path: "/home/monitoring",
        name: "monitoring",
        component: Monitoring,
        meta: { requiresAuth: true, resource: "general", action: "can_view_monitoring" }
      },
      {
        path: "/home/data-correction",
        name: "data-correction",
        component: () => import("@/domains/home/services/data-correction/DataCorrection.vue"),
        redirect: "/home/data-correction/start",
        meta: { requiresAuth: true, resource: "general", action: "can_perform_data_correction" },
        children: [
          {
            path: "/home/data-correction/start",
            name: "data-correction-start",
            component: () => import("@/domains/home/services/data-correction/DataCorrectionWizard.vue"),
            meta: { requiresAuth: true, resource: "general", action: "can_perform_data_correction" }
          },
          {
            path: "/home/data-correction/applications",
            name: "data-correction-applications",
            component: () => import("@/domains/home/services/data-correction/DataCorrectionApplications.vue"),
            meta: { requiresAuth: true, resource: "general", action: "can_perform_data_correction" }
          }
        ]
      },
    ]
  },
  {
    path: "/error-404",
    component: Error404,
    name: "error-404",
    meta: {
      title: "Error-404 ",
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: "error-404" },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, left: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const current_user = sessionStorage.getItem("current_user")

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (current_user === null) {
      sessionStorage.clear()
      next({
        name: "login",
        params: { nextUrl: to.fullPath },
      })
    } else {
      const deepestRoute = to.matched[to.matched.length - 1]
      const action = deepestRoute && deepestRoute.meta ? deepestRoute.meta.action as string : null
      const resource = deepestRoute && deepestRoute.meta && deepestRoute.meta.resource ? deepestRoute.meta.resource as string : "general"
      const canNavigate = action ? abilities.can(action, resource) : true

      if (!canNavigate) {
        if (to.name !== "forbidden") {
          return next({ name: "forbidden" })
        }
        return next()
      }

      const user = JSON.parse(current_user)
      if (user !== null) {
        next()
      } else {
        next({ name: "Home" })
      }
    }
  } else {
    if (current_user === null) {
      sessionStorage.clear()
      next()
    } else {
      next({
        name: "Home",
      })
    }
  }
})

const DEFAULT_TITLE = "OBRS Admin Portal"
router.afterEach(() => {
  document.title = DEFAULT_TITLE
})

export default router
